import type { ReactNode } from "react"
import { noteTilt, noteTint } from "@/utils/noteTints"
import styles from "@/styles/components/PaperNote.module.css"

/*
 * A paper note.
 *
 * The whole Pains round is written on these, and so are the facilitator's
 * pre-loaded notes and the Join screen's seat card. It is the one place the
 * product is deliberately not a screen: hand-written type on a tinted square,
 * tilted, with a strip of tape at the top, because that is what the physical
 * workshop produces and what the room is being asked to reproduce.
 *
 * Tint and tilt come off the note's `position`, cycled — so a board looks
 * scattered and varied without anyone choosing a colour, and without a colour
 * being stored anywhere. `utils/noteTints.ts` owns that mapping and says what
 * "position" has to mean for a note not to repaint under the room; read it
 * before passing anything here.
 */

interface PaperNoteProps {
  children: ReactNode
  /**
   * The note's position in creation order — NOT its row. `noteTints.ts`
   * explains the difference and why the row is the wrong number; callers with a
   * re-sortable list look the position up rather than using the map index.
   */
  position?: number
  /**
   * `md` is the room's own note — square, and big enough that the handwriting
   * is the screen. `sm` is a note being referred to rather than written.
   * `card` is the Join screen's seat: a landing card, wider than it is tall,
   * with no tilt, because it is a form and a tilted form is unusable.
   */
  size?: "sm" | "md" | "card"
  /** Lifts and cursors the note. For voting, where the note IS the target. */
  onClick?: () => void
  /**
   * A rank badge pinned where the plain tape strip would otherwise sit — the
   * Pains tally's "The winner" / "Tied 1st" / "Runner-up" / "Third". Replaces
   * the tape rather than joining it: a note is either plain paper or it is
   * wearing this one label.
   */
  cornerTag?: ReactNode
  title?: string
  className?: string
}

export default function PaperNote({
  children,
  position = 0,
  size = "md",
  onClick,
  cornerTag,
  title,
  className,
}: PaperNoteProps) {
  const tilt = size === "card" ? 0 : noteTilt(position)
  const style = {
    // The seat card is always plain paper: it is a form, not one of a set, so
    // there is no position for it to take a colour from.
    background: size === "card" ? "var(--note-cream)" : noteTint(position),
    // Read back by the hover rule and the entry animation, so straightening the
    // note does not have to know which tilt it started from.
    "--note-tilt": `${tilt}deg`,
    transform: `rotate(${tilt}deg)`,
  } as React.CSSProperties
  const classes = [
    styles.note,
    styles[size],
    onClick ? styles.pressable : "",
    cornerTag ? styles.tagged : "",
    className,
  ]
    .filter(Boolean)
    .join(" ")

  const tag = cornerTag ? <span className={styles.cornerTag}>{cornerTag}</span> : null

  if (!onClick) {
    return (
      <div className={classes} style={style} title={title}>
        {tag}
        {children}
      </div>
    )
  }

  return (
    <button type="button" className={classes} style={style} title={title} onClick={onClick}>
      {tag}
      {children}
    </button>
  )
}
